import { Check } from "lucide-react";
import { STEP_DEFS } from "../../../lib/journey";

// Small form parts shared by the Templates screen. The wizard pieces follow the look of the
// Book a service screens: numbered step pills, then a Back and Continue row.

export const focusRing = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2667B5] focus-visible:ring-offset-1";

// A row of choices where one is on, drawn as pills like the booking screens' chips.
export function Chips({ label, value, onChange, options }) {
  return (
    <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-2">
      {options.map(([v, text]) => {
        const on = value === v;
        return (
          <button
            key={v}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(v)}
            className={`h-[40px] cursor-pointer rounded-full px-5 text-[14px] font-medium transition-colors duration-150 ${focusRing} ${
              on ? "bg-[#111827] text-white" : "bg-[#F3F4F6] text-[#374151] hover:bg-[#E5E7EB]"
            }`}
          >
            {text}
          </button>
        );
      })}
    </div>
  );
}

// A label, the control, and the problem under it when there is one.
export function Field({ label, htmlFor, error, count, max, optional, children }) {
  return (
    <div>
      <div className="mb-[7px] flex items-baseline justify-between gap-3">
        <label htmlFor={htmlFor} className="text-[13.5px] font-semibold text-[#374151]">
          {label}
          {optional && <span className="ml-1 font-normal text-[#9CA3AF]">optional</span>}
        </label>
        {max ? (
          <span className={`text-[12px] tabular-nums ${count > max * 0.9 ? "text-[#B45309]" : "text-[#9CA3AF]"}`} aria-hidden="true">
            {count}/{max}
          </span>
        ) : null}
      </div>
      {children}
      {error ? <p className="mt-[6px] text-[12.5px] text-[#DC2626]">{error}</p> : null}
    </div>
  );
}

// The row of numbered step pills. Steps already done can be clicked to go back to.
export function StepPills({ steps, current, onGo }) {
  return (
    <ol className="m-0 flex list-none flex-wrap gap-[6px] p-0">
      {steps.map((s, i) => {
        const active = i === current;
        const done = i < current;
        const body = (
          <>
            <span
              className={`flex h-[26px] w-[26px] items-center justify-center rounded-full text-[13px] ${
                active ? "bg-white text-[#2667B5]" : done ? "bg-[#16A34A] text-white" : "bg-white text-[#6B7280]"
              }`}
            >
              {done ? <Check size={13} strokeWidth={3} aria-hidden="true" /> : i + 1}
            </span>
            {s.label}
          </>
        );
        const tone = active ? "bg-[#2667B5] text-white" : done ? "bg-[#F3F4F6] text-[#111827]" : "bg-[#F3F4F6] text-[#6B7280]";
        const shape = "flex h-[38px] items-center gap-2 rounded-full pl-[6px] pr-[14px] text-[14px] font-semibold";
        return (
          <li key={s.key} aria-current={active ? "step" : undefined}>
            {done ? (
              <button type="button" onClick={() => onGo(i)} className={`${shape} ${tone} cursor-pointer transition-colors duration-150 hover:bg-[#E5E7EB] ${focusRing}`}>
                {body}
              </button>
            ) : (
              <span className={`${shape} ${tone}`}>{body}</span>
            )}
          </li>
        );
      })}
    </ol>
  );
}

// Back on the left, the step's main button on the right.
export const WizardFoot = ({ children }) => (
  <div className="mt-2 flex items-center justify-between gap-3 border-t border-[#E5E7EB] pt-5">{children}</div>
);

export const ReviewList = ({ rows }) => (
  <dl className="m-0 text-[14.5px]">
    {rows.map(([k, v], i) => (
      <div key={k} className={`flex gap-4 py-[11px] ${i > 0 ? "border-t border-[#EEF0F3]" : ""}`}>
        <dt className="w-24 shrink-0 text-[#6B7280]">{k}</dt>
        <dd className="m-0 min-w-0 whitespace-pre-wrap break-words font-medium text-[#111827]">{v}</dd>
      </div>
    ))}
  </dl>
);

// The message steps a template can be given to, grouped under their Service journey stage
// (in journey order), so a stage the brand added shows with the rest.
export function StepOptionGroups({ steps }) {
  const groups = [];
  (steps || []).forEach((s) => {
    const name = s.custom
      ? s.label
      : s.stage_label || STEP_DEFS.find((d) => d.key === s.stage)?.label || (s.stage ? s.stage : "Not tied to a stage");
    const last = groups[groups.length - 1];
    if (last && last.name === name) last.items.push(s);
    else groups.push({ name, items: [s] });
  });
  return groups.map((g) => (
    <optgroup key={g.name} label={g.name}>
      {g.items.map((s) => (
        <option key={s.key} value={s.key}>
          {s.label}
        </option>
      ))}
    </optgroup>
  ));
}
